import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

function DbCard({
  db,
  isActive,
  isClosed,
  label,
  countLabel,
  canDelete,
  onSelect,
  onRequestDelete,
  selectMode,
  isChecked,
  onToggleCheck,
  t,
}) {
  return (
    <div className={`dbTabRow${isClosed ? " isArchived" : ""}${selectMode ? " isSelectMode" : ""}${isChecked ? " isChecked" : ""}`}>
      {selectMode && (
        <label
          className="dbTabCheckboxWrap"
          onClick={(e) => e.stopPropagation()}
          title={t("admin.bulkSelectThisDb", { defaultValue: "Select this database" })}
        >
          <input
            type="checkbox"
            className="dbTabCheckbox"
            checked={isChecked}
            onChange={onToggleCheck}
            aria-label={t("admin.bulkSelectThisDb", { defaultValue: "Select this database" })}
          />
        </label>
      )}

      <button
        type="button"
        className={`dbTab${isActive ? " isActive" : ""}${isClosed ? " isClosed" : ""}`}
        onClick={selectMode ? onToggleCheck : onSelect}
      >
        <span className="dbTabLabel">
          <span className="dbTabLabelText">{label}</span>
          {isActive && !isClosed && !selectMode && (
            <span className="dbTabLabelCount">{countLabel}</span>
          )}
        </span>
      </button>

      {!selectMode && canDelete && isClosed && (
        <button
          type="button"
          className="dbTabRestoreBtn"
          title={t("admin.restoreDatabase", { defaultValue: "Restore database" })}
          onClick={(e) => { e.stopPropagation(); onRequestDelete({ id: db._id, label, isClosed }); }}
        >
          ↩
        </button>
      )}

      {!selectMode && canDelete && (
        <button
          type="button"
          className="dbTabDeleteBtn"
          title={t("admin.removeDatabase", { defaultValue: "Remove database" })}
          onClick={(e) => { e.stopPropagation(); onRequestDelete({ id: db._id, label, isClosed }); }}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function DatabaseStackRail({
  databases,
  selectedDatabaseId,
  onSelectDatabase,
  getDbId,
  getDbLabel,
  searchQuery,
  onSearchQueryChange,
  indexesCount,
  loading,
  canDelete,
  onRequestDelete,
  onCreateDatabase,
  onExportBundle,
  onImportBundle,
  importBundleFileRef,
  // bulk-select props
  selectMode,
  onToggleSelectMode,
  selectedIds,
  onToggleSelectId,
  onSelectAllVisible,
  onClearSelection,
  onRequestBulkDelete,
}) {
  const { t } = useTranslation();

  const { active, archived, visibleIds } = useMemo(() => {
    if (!databases) return { active: [], archived: [], visibleIds: [] };
    const q = (searchQuery || "").toLowerCase();
    const all = q
      ? databases.filter((db) => {
          const id = String(getDbId(db) || "");
          const label = String(getDbLabel(db) || id);
          return id.toLowerCase().includes(q) || label.toLowerCase().includes(q);
        })
      : databases;
    return {
      active: all.filter((db) => !(db.isClosed || db.IsClosed)),
      archived: all.filter((db) => db.isClosed || db.IsClosed),
      visibleIds: all.map((db) => String(getDbId(db))),
    };
  }, [databases, searchQuery, getDbId, getDbLabel]);

  const countLabel = t("admin.indexCount", {
    count: indexesCount,
    defaultValue: `${indexesCount} index(es)`,
  });

  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map(String)),
    [selectedIds]
  );

  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selectedSet.has(id)).length,
    [visibleIds, selectedSet]
  );
  const allVisibleChecked = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  function renderCard(db) {
    const id = getDbId(db);
    const idStr = String(id);
    const isClosed = db.isClosed || db.IsClosed || false;
    const label = getDbLabel(db) || id;
    return (
      <DbCard
        key={id}
        db={{ ...db, _id: id }}
        isActive={id === selectedDatabaseId}
        isClosed={isClosed}
        label={label}
        countLabel={countLabel}
        canDelete={canDelete}
        onSelect={() => onSelectDatabase(id)}
        onRequestDelete={onRequestDelete}
        selectMode={selectMode}
        isChecked={selectedSet.has(idStr)}
        onToggleCheck={() => onToggleSelectId && onToggleSelectId(idStr)}
        t={t}
      />
    );
  }

  const isEmpty = active.length === 0 && archived.length === 0;
  const selectedCount = selectedSet.size;

  return (
    <div className="db-column dbRail">
      <div className="dbRailHeader">
        <div className="db-stack-header">
          <div className="db-track-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{t("admin.database", { defaultValue: "Databases" })}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {canDelete && onToggleSelectMode && (
                <button
                  type="button"
                  className={`dbRailCreateBtn${selectMode ? " dbRailSelectBtnActive" : ""}`}
                  title={
                    selectMode
                      ? t("admin.bulkExitSelect", { defaultValue: "Exit selection mode" })
                      : t("admin.bulkEnterSelect", { defaultValue: "Select multiple databases" })
                  }
                  onClick={onToggleSelectMode}
                  style={{ fontSize: 0, lineHeight: 0 }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
                    <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
                    <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M9.5 11.5l1.5 1.5L14 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              {!selectMode && onExportBundle && (
                <button
                  type="button"
                  className="dbRailCreateBtn"
                  title={t("admin.exportBundle", { defaultValue: "Export Bundle (multiple databases)" })}
                  onClick={onExportBundle}
                  style={{ fontSize: 0, lineHeight: 0 }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 2h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                    <path d="M9 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                    <path d="M8 8.5v4M6.5 11l1.5 1.5L9.5 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              {!selectMode && onImportBundle && (
                <>
                  <button
                    type="button"
                    className="dbRailCreateBtn"
                    title={t("admin.importBundle", { defaultValue: "Import Bundle (multiple databases)" })}
                    onClick={() => importBundleFileRef?.current?.click()}
                    style={{ fontSize: 0, lineHeight: 0 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 2h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                      <path d="M9 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                      <path d="M8 12.5v-4M6.5 10l1.5-1.5L9.5 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <input
                    ref={importBundleFileRef}
                    type="file"
                    accept=".json,.depbundle.json"
                    style={{ display: "none" }}
                    onChange={onImportBundle}
                  />
                </>
              )}
              {!selectMode && onCreateDatabase && (
                <button
                  type="button"
                  className="dbRailCreateBtn"
                  title={t("admin.createDatabase", { defaultValue: "Create new database" })}
                  onClick={onCreateDatabase}
                >+</button>
              )}
            </div>
          </div>
          <div className="db-stack-search-wrapper">
            <input
              className="db-stack-search"
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder={t("admin.searchDatabases", { defaultValue: "Search databases..." })}
            />
          </div>

          {selectMode && (
            <div className="dbRailSelectToolbar">
              <span className="dbRailSelectCount">
                {t("admin.bulkSelectedCount", { count: selectedCount, defaultValue: `${selectedCount} database(s) selected` })}
              </span>
              <div className="dbRailSelectToolbarBtns">
                <button
                  type="button"
                  className="link-btn"
                  onClick={() =>
                    allVisibleChecked
                      ? onClearSelection && onClearSelection(visibleIds)
                      : onSelectAllVisible && onSelectAllVisible(visibleIds)
                  }
                  disabled={visibleIds.length === 0}
                >
                  {allVisibleChecked
                    ? t("admin.bulkClearVisible", { defaultValue: "Clear visible" })
                    : t("admin.bulkSelectAllVisible", { defaultValue: "Select all visible" })}
                </button>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => onClearSelection && onClearSelection()}
                  >
                    {t("admin.bulkClearAll", { defaultValue: "Clear all" })}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {loading && (!databases || databases.length === 0) && (
          <div className="db-track-empty">
            {t("admin.loading", { defaultValue: "Loading databases..." })}
          </div>
        )}
        {!loading && isEmpty && (
          <div className="db-track-empty">
            {t("admin.noDatabases", { defaultValue: "No databases are available." })}
          </div>
        )}
      </div>

      <div className="dbTabsRegion">
        <div className="dbTabStack">

          {/* ── Active databases ── */}
          {active.map(renderCard)}

          {/* ── Archived section ── */}
          {archived.length > 0 && (
            <>
              <div className="dbArchivedDivider">
                <span>{t("admin.archived", { defaultValue: "Archived" })}</span>
              </div>
              {archived.map(renderCard)}
            </>
          )}

        </div>
      </div>

      {selectMode && selectedCount > 0 && (
        <div className="dbRailBulkActionBar">
          <button
            type="button"
            className="dbRailBulkActionBtn dbRailBulkActionBtn--danger"
            onClick={onRequestBulkDelete}
            disabled={!canDelete}
          >
            {t("admin.bulkActionRemoveBtn", {
              count: selectedCount,
              defaultValue: `Remove ${selectedCount} database(s)…`,
            })}
          </button>
        </div>
      )}
    </div>
  );
}
